import React, { useState, useMemo, useRef } from 'react';
import './WealthChart.css';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatCompact = (amount) => {
  if (amount >= 10000000) return `${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
  return amount.toFixed(0);
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    month: 'short',
    day: 'numeric',
  });
};

const formatFullDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const WealthChart = ({ history }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const svgRef = useRef(null);

  const chartData = useMemo(() => {
    if (!history || history.length === 0) return null;

    const sorted = [...history].sort(
      (a, b) => new Date(a.recorded_at) - new Date(b.recorded_at)
    );

    const values = sorted.map((e) => parseFloat(e.total_wealth) || 0);
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const range = maxVal - minVal || 1;
    const padding = range * 0.1;

    return {
      entries: sorted,
      values,
      minVal: minVal - padding,
      maxVal: maxVal + padding,
      range: range + padding * 2,
      overallChange: values.length >= 2 ? values[values.length - 1] - values[0] : 0,
    };
  }, [history]);

  if (!chartData || chartData.entries.length < 2) {
    return (
      <div className="wealth-chart-empty">
        <p>Record at least 2 snapshots to see the wealth trend graph</p>
      </div>
    );
  }

  const { entries, values, minVal, range, overallChange } = chartData;
  const width = 800;
  const height = 300;
  const marginTop = 20;
  const marginBottom = 40;
  const marginLeft = 70;
  const marginRight = 20;
  const chartWidth = width - marginLeft - marginRight;
  const chartHeight = height - marginTop - marginBottom;

  const getX = (i) => marginLeft + (i / (entries.length - 1)) * chartWidth;
  const getY = (val) =>
    marginTop + chartHeight - ((val - minVal) / range) * chartHeight;

  const linePath = values
    .map((val, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(val)}`)
    .join(' ');

  const areaPath = `${linePath} L ${getX(values.length - 1)} ${marginTop + chartHeight} L ${getX(0)} ${marginTop + chartHeight} Z`;

  const isPositive = overallChange >= 0;
  const gradientId = isPositive ? 'chartGradientUp' : 'chartGradientDown';
  const lineColor = isPositive ? '#10b981' : '#ef4444';

  const yTickCount = 5;
  const yTicks = Array.from({ length: yTickCount }, (_, i) => {
    const val = minVal + (range / (yTickCount - 1)) * i;
    return { val, y: getY(val) };
  });

  const xLabelStep = Math.max(1, Math.floor(entries.length / 6));

  const segments = [];
  for (let i = 1; i < values.length; i++) {
    const change = values[i] - values[i - 1];
    segments.push({
      x1: getX(i - 1),
      y1: getY(values[i - 1]),
      x2: getX(i),
      y2: getY(values[i]),
      color: change >= 0 ? '#10b981' : '#ef4444',
    });
  }

  return (
    <div className="wealth-chart-container">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        className="wealth-chart-svg"
      >
        <defs>
          <linearGradient id="chartGradientUp" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="chartGradientDown" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.02" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Grid lines */}
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line
              x1={marginLeft}
              y1={tick.y}
              x2={width - marginRight}
              y2={tick.y}
              className="chart-grid-line"
            />
            <text
              x={marginLeft - 10}
              y={tick.y + 4}
              className="chart-y-label"
              textAnchor="end"
            >
              {formatCompact(tick.val)}
            </text>
          </g>
        ))}

        {/* Area fill */}
        <path d={areaPath} fill={`url(#${gradientId})`} />

        {/* Colored line segments */}
        {segments.map((seg, i) => (
          <line
            key={i}
            x1={seg.x1}
            y1={seg.y1}
            x2={seg.x2}
            y2={seg.y2}
            stroke={seg.color}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        ))}

        {/* Main line overlay for smooth appearance */}
        <path
          d={linePath}
          fill="none"
          stroke={lineColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.3"
        />

        {/* X-axis labels */}
        {entries.map((entry, i) => {
          if (i % xLabelStep !== 0 && i !== entries.length - 1) return null;
          return (
            <text
              key={i}
              x={getX(i)}
              y={height - 8}
              className="chart-x-label"
              textAnchor="middle"
            >
              {formatDate(entry.recorded_at)}
            </text>
          );
        })}

        {/* Data points */}
        {values.map((val, i) => {
          const pointColor =
            i === 0
              ? lineColor
              : values[i] >= values[i - 1]
              ? '#10b981'
              : '#ef4444';
          return (
            <g key={i}>
              <circle
                cx={getX(i)}
                cy={getY(val)}
                r={hoveredIndex === i ? 6 : 4}
                fill={pointColor}
                stroke="var(--card-bg, #fff)"
                strokeWidth="2"
                className="chart-point"
                filter={hoveredIndex === i ? 'url(#glow)' : undefined}
              />
              {/* Invisible larger hit area */}
              <circle
                cx={getX(i)}
                cy={getY(val)}
                r={20}
                fill="transparent"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{ cursor: 'pointer' }}
              />
            </g>
          );
        })}

        {/* Hover tooltip */}
        {hoveredIndex !== null && (() => {
          const x = getX(hoveredIndex);
          const y = getY(values[hoveredIndex]);
          const change =
            hoveredIndex > 0
              ? values[hoveredIndex] - values[hoveredIndex - 1]
              : 0;
          const changePercent =
            hoveredIndex > 0 && values[hoveredIndex - 1] !== 0
              ? (change / values[hoveredIndex - 1]) * 100
              : 0;

          const tooltipWidth = 180;
          const tooltipHeight = 60;
          let tooltipX = x - tooltipWidth / 2;
          let tooltipY = y - tooltipHeight - 15;
          if (tooltipX < marginLeft) tooltipX = marginLeft;
          if (tooltipX + tooltipWidth > width - marginRight)
            tooltipX = width - marginRight - tooltipWidth;
          if (tooltipY < 0) tooltipY = y + 15;

          return (
            <g className="chart-tooltip-group">
              <line
                x1={x}
                y1={marginTop}
                x2={x}
                y2={marginTop + chartHeight}
                className="chart-crosshair"
              />
              <rect
                x={tooltipX}
                y={tooltipY}
                width={tooltipWidth}
                height={tooltipHeight}
                rx="8"
                className="chart-tooltip-bg"
              />
              <text
                x={tooltipX + 10}
                y={tooltipY + 20}
                className="chart-tooltip-value"
              >
                {formatCurrency(values[hoveredIndex])}
              </text>
              <text
                x={tooltipX + 10}
                y={tooltipY + 38}
                className={`chart-tooltip-change ${change >= 0 ? 'positive' : 'negative'}`}
              >
                {hoveredIndex > 0
                  ? `${change >= 0 ? '+' : ''}${formatCurrency(change)} (${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(1)}%)`
                  : 'First snapshot'}
              </text>
              <text
                x={tooltipX + 10}
                y={tooltipY + 52}
                className="chart-tooltip-date"
              >
                {formatFullDate(entries[hoveredIndex].recorded_at)}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
};

export default WealthChart;
